import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCompanyLists } from '../../../services/queries/SourcingCompanyQueries';
import { ICompanyListsResponse } from '../../../schemas/CompanyProfile.schema';
import { SOURCING_COMPANY_LISTS_KEY } from '../../../services/queries-hooks/SourcingListsQueriesHooks';
import { resolveCompanyLookup } from './resolveCompanyLookup';

// Re-export so consumers in this module's neighborhood can keep importing
// from a single page-level entrypoint. The canonical source is the service
// layer (where it can be invalidated by mutations without a layering loop).
export const SOURCING_COMPANY_LISTS_QUERY_KEY = SOURCING_COMPANY_LISTS_KEY;

// List membership rarely changes mid-session — but we still pick a short
// stale time so any "Add to List" mutation that invalidates this key gets
// reflected on the header without forcing a full refetch on every render.
const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Fetches the set of saved lists that already contain the company shown
 * on the sourcing profile page. Mirrors the lookup contract used by
 * `useSourcingProfile` so the membership panel can load independently
 * of the merged profile.
 *
 * Failures are not toasted: an empty list is a valid "no matches" or
 * "could not resolve" state and the UI degrades to "show all lists as
 * candidates", which matches the integration guide's recommendation.
 */
export function useSourcingCompanyLists(companyId: string) {
  const lookup = useMemo(() => resolveCompanyLookup(companyId), [companyId]);
  const hasLookup = Object.keys(lookup).length > 0;

  return useQuery<ICompanyListsResponse>({
    queryKey: [SOURCING_COMPANY_LISTS_KEY, companyId],
    queryFn: ({ signal }) => fetchCompanyLists(lookup, signal),
    staleTime: FIVE_MINUTES,
    enabled: Boolean(companyId) && hasLookup,
  });
}
